/*
  MenoGlow mobile product-gallery slider.

  Why this exists:
  The Dawn product gallery uses a native CSS scroll-snap slider on mobile
  (`.slider--mobile` + SliderComponent in global.js). On this heavily
  customized, third-party-script-heavy PDP that slider snaps unreliably —
  swipes drift between photos instead of locking to one. This replaces the
  scroll mechanism on mobile with a deterministic transform (translateX)
  track: the finger drives translateX, and on release we always settle on a
  whole slide. Desktop (>=750px) is left completely untouched.

  Self-contained on purpose: no external library, no dependency on the native
  scroll behaviour, so unrelated scripts on the page can't break it.
*/
(function () {
  'use strict';

  var MOBILE_MQ = window.matchMedia('(max-width: 749px)');
  var SWIPE_RATIO = 0.15; // fraction of viewport a drag must cross to advance
  var FLICK_VELOCITY = 0.4; // px/ms — a fast flick advances regardless of distance
  var EASE = 'transform 0.35s cubic-bezier(0.22, 0.61, 0.36, 1)';

  function MGSlider(gallery) {
    this.gallery = gallery;
    this.viewer =
      gallery.querySelector('slider-component[id^="GalleryViewer"]') ||
      gallery.querySelector('slider-component');
    this.track = this.viewer && this.viewer.querySelector('ul[id^="Slider-Gallery"]');
    this.slides = this.track
      ? Array.prototype.slice.call(this.track.querySelectorAll('.slider__slide'))
      : [];
    if (!this.viewer || !this.track || this.slides.length < 2) return;

    this.index = Math.max(0, this.slides.findIndex(function (s) {
      return s.classList.contains('is-active');
    }));
    if (this.index < 0) this.index = 0;

    this.active = false;
    this.dragging = false;
    this.axisLock = null; // 'x' | 'y' | null

    this._onResize = this._onResize.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);

    this._buildControls();
    this._bindArrows();

    // React to crossing the mobile/desktop breakpoint.
    if (MOBILE_MQ.addEventListener) {
      MOBILE_MQ.addEventListener('change', this._onResize);
    } else if (MOBILE_MQ.addListener) {
      MOBILE_MQ.addListener(this._onResize);
    }
    window.addEventListener('resize', this._onResize);

    if (MOBILE_MQ.matches) this.activate();
  }

  MGSlider.prototype.activate = function () {
    if (this.active) return;
    this.active = true;
    this.gallery.classList.add('mg-swipe-on');
    this._prepareSlides();
    this.track.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.track.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this.track.addEventListener('touchend', this._onTouchEnd, { passive: true });
    this.track.addEventListener('touchcancel', this._onTouchEnd, { passive: true });
    this.render(false);
  };

  MGSlider.prototype.deactivate = function () {
    if (!this.active) return;
    this.active = false;
    this.gallery.classList.remove('mg-swipe-on');
    this.track.removeEventListener('touchstart', this._onTouchStart);
    this.track.removeEventListener('touchmove', this._onTouchMove);
    this.track.removeEventListener('touchend', this._onTouchEnd);
    this.track.removeEventListener('touchcancel', this._onTouchEnd);
    // Hand styling back to the native desktop layout.
    this.track.style.transition = '';
    this.track.style.transform = '';
    this.viewer.style.height = '';
  };

  MGSlider.prototype._prepareSlides = function () {
    if (this._prepared) return;
    this._prepared = true;
    var self = this;
    this.slides.forEach(function (slide) {
      // Dawn's reveal-on-scroll keeps non-first slides at opacity:0 until its
      // IntersectionObserver fires on scroll. We move slides with transform,
      // which never triggers that — so disable the reveal for gallery slides.
      slide.classList.remove('scroll-trigger', 'scroll-trigger--offscreen', 'animate--fade-in');
      slide.style.opacity = '1';
      slide.style.transform = 'none';
      var img = slide.querySelector('img');
      if (!img) return;
      // Lazy images only load on scroll intersection; eager-load them so every
      // slide is ready the instant we swipe to it.
      if (img.getAttribute('loading') === 'lazy') {
        img.setAttribute('loading', 'eager');
        if (!img.complete) {
          var src = img.getAttribute('src');
          if (src) img.setAttribute('src', src);
        }
      }
      // Once an image knows its size, refresh the viewport height if it's active.
      img.addEventListener('load', function () {
        if (self.active) self._setViewportHeight();
      });
    });
  };

  MGSlider.prototype._onResize = function () {
    if (MOBILE_MQ.matches) {
      this.activate();
      this.render(false);
    } else {
      this.deactivate();
    }
  };

  MGSlider.prototype.width = function () {
    return this.viewer.clientWidth || this.track.clientWidth || 1;
  };

  MGSlider.prototype.goTo = function (i, animate) {
    this.index = Math.max(0, Math.min(this.slides.length - 1, i));
    this.render(animate !== false);
    this._syncActive();
  };

  MGSlider.prototype.render = function (animate) {
    if (!this.active) return;
    this.track.style.transition = animate ? EASE : 'none';
    // Percentage (not pixels): each slide is 100% of the track, so this is
    // always an exact whole-slide step even if the viewport width changes
    // (e.g. a scrollbar appearing) — no cumulative drift possible.
    this.track.style.transform = 'translateX(' + -this.index * 100 + '%)';
    this._setViewportHeight();
    this._updateControls();
  };

  // Images have different aspect ratios; let the viewport follow the active
  // slide so each photo shows in full (CSS animates the height change).
  MGSlider.prototype._setViewportHeight = function () {
    var slide = this.slides[this.index];
    if (slide) this.viewer.style.height = slide.offsetHeight + 'px';
  };

  // --- touch handling -------------------------------------------------------

  MGSlider.prototype._onTouchStart = function (e) {
    if (e.touches.length !== 1) return;
    this.dragging = true;
    this.axisLock = null;
    this.startX = e.touches[0].clientX;
    this.startY = e.touches[0].clientY;
    this.startT = e.timeStamp;
    this.basePx = -this.index * this.width();
    this.track.style.transition = 'none';
  };

  MGSlider.prototype._onTouchMove = function (e) {
    if (!this.dragging) return;
    var dx = e.touches[0].clientX - this.startX;
    var dy = e.touches[0].clientY - this.startY;

    // Decide intent once, then commit to it for the gesture.
    if (this.axisLock === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      this.axisLock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (this.axisLock === 'y') return; // let the page scroll vertically

    e.preventDefault(); // we own the horizontal gesture now
    var max = -(this.slides.length - 1) * this.width();
    var pos = this.basePx + dx;
    // Rubber-band resistance past the first/last slide.
    if (pos > 0) pos = pos * 0.35;
    else if (pos < max) pos = max + (pos - max) * 0.35;
    this.track.style.transform = 'translateX(' + pos + 'px)';
  };

  MGSlider.prototype._onTouchEnd = function (e) {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.axisLock !== 'x') return;

    var endX = (e.changedTouches && e.changedTouches[0].clientX) || this.startX;
    var dx = endX - this.startX;
    var dt = Math.max(1, e.timeStamp - this.startT);
    var velocity = dx / dt; // px per ms
    var w = this.width();

    var step = 0;
    if (Math.abs(velocity) > FLICK_VELOCITY) {
      step = velocity < 0 ? 1 : -1;
    } else if (Math.abs(dx) > w * SWIPE_RATIO) {
      step = dx < 0 ? 1 : -1;
    }
    this.goTo(this.index + step, true);
  };

  // --- controls (arrows + dots) --------------------------------------------

  MGSlider.prototype._bindArrows = function () {
    var self = this;
    // The native SliderComponent has click handlers on these buttons that
    // scrollTo() the (now non-scrolling) track. Replace the buttons with
    // clones to strip those listeners, then drive them ourselves.
    ['prev', 'next'].forEach(function (dir) {
      var btn = self.viewer.querySelector('.slider-button--' + dir);
      if (!btn) return;
      var clone = btn.cloneNode(true);
      btn.parentNode.replaceChild(clone, btn);
      clone.addEventListener('click', function (ev) {
        ev.preventDefault();
        if (!self.active) return;
        self.goTo(self.index + (dir === 'next' ? 1 : -1), true);
      });
      self[dir + 'Btn'] = clone;
    });
  };

  MGSlider.prototype._buildControls = function () {
    var self = this;
    var dots = document.createElement('div');
    dots.className = 'mg-dots';
    this.dots = this.slides.map(function (slide, i) {
      var d = document.createElement('button');
      d.type = 'button';
      d.className = 'mg-dot';
      d.setAttribute('aria-label', 'Rodyti ' + (i + 1) + ' nuotrauką');
      d.addEventListener('click', function () {
        self.goTo(i, true);
      });
      dots.appendChild(d);
      return d;
    });
    // Place the dots right after the slider viewport.
    this.viewer.insertAdjacentElement('afterend', dots);
    this.dotsEl = dots;
  };

  MGSlider.prototype._updateControls = function () {
    if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.index >= this.slides.length - 1;
    if (this.dots) {
      this.dots.forEach(function (d, i) {
        var active = i === this.index;
        d.classList.toggle('is-active', active);
        // aria-current is valid on a plain button (aria-selected is not — it
        // requires role=tab, which these dots intentionally don't use).
        if (active) {
          d.setAttribute('aria-current', 'true');
        } else {
          d.removeAttribute('aria-current');
        }
      }, this);
    }
  };

  MGSlider.prototype._syncActive = function () {
    // Keep Dawn's `.is-active` slide in sync so the zoom modal opens on the
    // photo currently in view.
    this.slides.forEach(function (s, i) {
      s.classList.toggle('is-active', i === this.index);
    }, this);
  };

  // --- bootstrap ------------------------------------------------------------

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('media-gallery').forEach(function (gallery) {
      if (gallery.__mgSlider) return;
      var inst = new MGSlider(gallery);
      if (inst.track) gallery.__mgSlider = inst;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Re-init when the theme editor reloads the product section.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
